import { select, event } from 'd3-selection';
import { scaleThreshold } from 'd3-scale';
import { geoMercator } from 'd3-geo';
import { tile as geoTile } from 'd3-tile';
import { zoom as geoZoom, zoomIdentity as geoZoomIdentity } from 'd3-zoom';

const defaults = {
  target: '#map',
  data: [],
  initialCenter: [10.4, 51.6],      // center of the map (long, lat)
  initialScale: 1 << 14,            // intial map zoom (2^n)
  zoomScale: 1 << 24,               // zoom level when focused on an elevator
  scaleExtent: [1 << 14, 1 << 24],  // possible map zoom levels (2^n)
  animationSpeed: 500               // animation speed (miliseconds)
};

export function init(config = {}) {
  // Sort elevators by worst performance
  config.data = config.data.sort((a, b) => a.available - b.available);

  // harmonize config with defaults
  config = Object.assign({}, defaults, config);

  const { target, data } = config;
  const container = select(target);

  let center = config.initialCenter;
  let scale = config.initialScale;

  let projection;
  let tile;
  let tilegrid;

  let context;
  let animationInstance;
  let timerInstance;

  let pixelRatio;
  let width;
  let height;

  let animate = false;
  let state;
  let property;
  let color;
  let index;
  let equipmentnumber;

  return {
    draw: () => draw(),
    drawDots: (property, color, index) => drawDots(property, color, index),
    drawAnimatedDots: (property, color) => drawAnimatedDots(property, color),
    stopAnimation: () => stopAnimation(),
    zoomTo: (coord, scale) => zoomTo(coord, scale),
    update: (elevator) => {
      if (elevator !== undefined) {
        zoomTo([elevator.lon, elevator.lat], config.zoomScale);
        property = 'available';
        equipmentnumber = elevator.equipmentnumber;
        color = scaleThreshold()
          .domain([1-24/365, 1-14/365, 1-7/365])
          .range([
            'rgba(240, 59, 32, 0.75)',     // red
            'rgba(254, 178, 76, 0.75)',   // orange
            'rgba(255, 237, 160, 0.75)',  // yellow
            'rgba(240, 240, 240, 0.7)'   // light grey
          ]);
      }
      draw();
      eval('this.draw' + state + '(property, color, index)');
    }
  };

  function draw() {
    const bounds = container.node().getBoundingClientRect();
    pixelRatio = window.devicePixelRatio;
    width = bounds.width;
    height = bounds.height;

    drawBaselayer();
    drawCanvas();
  }

  function drawDots(propertyName, colorScale, i = undefined) {
    property = propertyName;
    index = i;
    color = colorScale;
    state = 'Dots';

    if (animationInstance !== undefined) {
      cancelAnimationFrame(animationInstance);
    }

    context.clearRect(0, 0, width, height);

    if (scale === config.initialScale) {
      data.forEach(d => drawDot(d, index));
    }
    else {
      data.forEach(d => drawDot(d, index, 5));
    }

  }

  function drawAnimatedDots(propertyName, colorScale) {
    property = propertyName;
    color = colorScale;
    state = 'AnimatedDots';
    animateDots();

    function animateDots () {
      let currentDay = 0;
      let maxDays = data[0][propertyName].length;

      stopAnimation();
      animate = true;

      (function animationLoop() {
        context.clearRect(0, 0, width, height);
        data.forEach(d => drawDot(d, currentDay));
        currentDay++;

        if (currentDay < maxDays && animate) {
          timerInstance = setTimeout(function() {
            animationInstance = requestAnimationFrame(animationLoop);
          }, config.animationSpeed);
        } else {
          cancelAnimationFrame(animationInstance);

          if (animate) {
            animateDots();
          }
        }
      })();
    }
  }

  function stopAnimation() {
    cancelAnimationFrame(animationInstance);
    clearTimeout(timerInstance);
    animate = false;
  }

  function drawDot(d, i, r = 3){

    context.beginPath();
    // context.arc(x, y, radius, startAngle, endAngle, anticlockwise);
    context.arc(
      projection([d.lon, d.lat])[0],
      projection([d.lon, d.lat])[1],
      r,
      0,
      2 * Math.PI
    );

    if (i === undefined) {
      context.fillStyle = color(d[property]);
    }
    else {
      context.fillStyle = color(d[property][i]);
    }

    context.fill();
    context.closePath();

    if (d.equipmentnumber === equipmentnumber) {
      context.beginPath();
      context.arc(
        projection([d.lon, d.lat])[0],
        projection([d.lon, d.lat])[1],
        11,
        0,
        2 * Math.PI
      );
      context.lineWidth = 2;
      context.strokeStyle = 'white';
      context.stroke();
      context.closePath();
    }
  }

  function drawCanvas() {
    container.select('canvas').remove();

    const canvas = container.append('canvas')
      .attr('class', 'overlay')
      .attr('width', width * pixelRatio)
      .attr('height', height * pixelRatio);

    context = canvas.node().getContext('2d');

    // Scale canvas by retina ratio
    context.scale(pixelRatio, pixelRatio);

    // Set blending mode for colors (multiply, screen, overlay, ...)
    context.globalCompositeOperation = 'hard-light';
  }

  function drawBaselayer() {
    const zoom = geoZoom()
      .scaleExtent(config.scaleExtent)
      .extent([[0, 0], [width, height]])
      .on('zoom', () => zoomed(event.transform));

    projection = geoMercator()
      .scale(1 / (2 * Math.PI))
      .translate([0, 0]);

    tile = geoTile()
      .extent([[0, 0], [width, height]])
      .tileSize(256);

    // Create the map
    const svg = container.select('.content')
      .html('')
      .attr('viewBox', [0, 0, width, height])
      .style('background', '#262626')
      .style('width', '100%');

    tilegrid = svg.append('g')
      .selectAll('image');

    svg
      // .call(zoom) // Enable mouse wheel zoom
      .call(zoom.transform, geoZoomIdentity
        .translate(width / 2, height / 2)
        .scale(-scale)
        .translate(...projection(center))
        .scale(-1)
      );
  }

  function zoomTo(coordTo, scaleTo) {
    center = coordTo;
    scale = scaleTo;

    if (scale === config.initialScale) {
      equipmentnumber = undefined;
    }
  }

  function zoomed(transform) {
    const tiles = tile(transform);

    tilegrid = tilegrid.data(tiles, d => d).join('image')
      .attr('xlink:href', d => getUrl(...d))
      .attr('x', ([x]) => (x + tiles.translate[0]) * tiles.scale)
      .attr('y', ([, y]) => (y + tiles.translate[1]) * tiles.scale)
      .attr('width', tiles.scale)
      .attr('height', tiles.scale);

    projection
      .scale(transform.k / (2 * Math.PI))
      .translate([transform.x, transform.y]);
  }

  // https://github.com/CartoDB/svg-styles
  function getUrl(x, y, z) {
    if (scale === config.initialScale) {
      // focus on whole Germany
      return `https://a.basemaps.cartocdn.com/dark_nolabels/${z}/${x}/${y}${pixelRatio > 1 ? '@2x' : ''}.png`;
    } else {
      // zoomed to a certain elevator / station
      return `https://a.basemaps.cartocdn.com/dark_all/${z}/${x}/${y}${pixelRatio > 1 ? '@2x' : ''}.png`;
    }
  }
}
